import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { getCurrencies, type CurrencyRate } from "./api";

const STORAGE_KEY = "neluminar-currency";

const FALLBACK_CURRENCIES: CurrencyRate[] = [
  { code: "RUB", name: "Российский рубль", symbol: "₽", rate_to_rub: 1, updated_at: null },
  { code: "USD", name: "Доллар США", symbol: "$", rate_to_rub: null, updated_at: null },
  { code: "EUR", name: "Евро", symbol: "€", rate_to_rub: null, updated_at: null },
  { code: "GBP", name: "Фунт стерлингов", symbol: "£", rate_to_rub: null, updated_at: null },
  { code: "CNY", name: "Китайский юань", symbol: "¥", rate_to_rub: null, updated_at: null },
  { code: "JPY", name: "Японская иена", symbol: "¥", rate_to_rub: null, updated_at: null },
  { code: "KZT", name: "Казахстанский тенге", symbol: "₸", rate_to_rub: null, updated_at: null },
  { code: "TRY", name: "Турецкая лира", symbol: "₺", rate_to_rub: null, updated_at: null },
  { code: "AED", name: "Дирхам ОАЭ", symbol: "د.إ", rate_to_rub: null, updated_at: null },
];

interface CurrencyContextValue {
  currencies: CurrencyRate[];
  currency: CurrencyRate;
  setCurrencyCode: (code: string) => void;
  toCurrency: (rubles: number) => number;
  toRubles: (amount: number) => number;
  formatPrice: (rubles: number | null, options?: { maximumFractionDigits?: number }) => string;
}

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

function loadInitialCurrency(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? "RUB";
  } catch {
    return "RUB";
  }
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currencyCode, setCurrencyCode] = useState(loadInitialCurrency);
  const currenciesQuery = useQuery({
    queryKey: ["currencies"],
    queryFn: ({ signal }) => getCurrencies(signal),
    staleTime: 55_000,
    refetchInterval: 60_000,
    retry: 1,
  });
  const currencies = currenciesQuery.data?.rows ?? FALLBACK_CURRENCIES;
  const ruble = currencies.find((item) => item.code === "RUB") ?? FALLBACK_CURRENCIES[0];
  const requested = currencies.find((item) => item.code === currencyCode);
  const currency = requested?.rate_to_rub ? requested : ruble;

  const selectCurrency = (code: string) => {
    setCurrencyCode(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // Persisting the selection is optional in private browser contexts.
    }
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, currency.code);
    } catch {
      // Persisting the selection is optional in private browser contexts.
    }
  }, [currency.code]);

  const rate = currency.rate_to_rub ?? 1;
  const toCurrency = (rubles: number) => rubles / rate;
  const value: CurrencyContextValue = {
      currencies,
      currency,
      setCurrencyCode: selectCurrency,
      toCurrency,
      toRubles: (amount: number) => amount * rate,
      formatPrice: (rubles, options) => {
        if (rubles === null || !Number.isFinite(rubles)) {
          return "—";
        }
      const amount = toCurrency(rubles);
      const maximumFractionDigits =
        options?.maximumFractionDigits ?? 2;
        return `${new Intl.NumberFormat("ru-RU", {
          minimumFractionDigits: 0,
          maximumFractionDigits,
        }).format(amount)} ${currency.symbol}`;
      },
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error("useCurrency must be used inside CurrencyProvider");
  }
  return context;
}
